import React, { useEffect, useState } from "react";
import { Button, Col, Row, Spinner, Stack } from "react-bootstrap";
import { useParams } from "react-router-dom";
import {
  fetchBeds,
  fetchEthnicities,
  fetchGenders,
  fetchJobs,
  fetchPrograms,
  fetchRaces,
  fetchRelationships,
  fetchRooms,
  patchBedOccupant,
  patchFieldValue,
  patchJobAssignment,
  patchMember,
  postBedOccupant,
  postFieldValue,
  postJobAssignment,
} from "../../../../services/httpClient";
import DatePicker from "../../../ui/DatePicker";
import Input from "../../../ui/Input";
import InputPhone from "../../../ui/InputPhone";
import SelectObject from "../../../ui/SelectObject";
import ConfirmDelete from "./ConfirmDelete";
import ConfirmBan from "../../../ui/ConfirmBan";
import useApp from "../../../../utils/useApp";
import SelectObjectTypeahead from "../../../ui/SelectObjectTypeahead";
import CustomField from "../../../ui/CustomField";
import CaptureProfilePic from "../../../ui/CaptureProfilePic";
import { UserFields } from "../../../../types/userFields";
import { validateUserFields } from "../../../../helpers/validation";

interface EditMemberState {
  user?: any;
  members: any;
  fetchHousehold: any;
  currentCase?: any;
  myCase: boolean;
  isLastUser: boolean;
  resetHouseholdTab: any;
  setUserToSave: any;
  onClickDoneEdit: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const EditMember = ({
  user,
  members,
  fetchHousehold,
  currentCase,
  myCase,
  isLastUser,
  resetHouseholdTab,
  setUserToSave,
  onClickDoneEdit,
  setShowToast,
  setToastText,
  setToastBody,
}: EditMemberState) => {
  const { id } = useParams();
  const { auth }: any = useApp();
  const heading = user?.relationship === "child" ? "Edit Child" : "Edit Adult";
  const [custom, setCustom] = useState<any[]>([]);
  const [profileImage, setProfileImage] = useState<string>("");
  const [saveIsEnabled, setSaveIsEnabled] = useState<boolean>(false);
  const [userUpdated, setUserUpdated] = useState<boolean>(false);
  const [customUpdated, setCustomUpdated] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState("");
  const [relationships, setRelationships] = useState<any[]>([]);
  const [genders, setGenders] = useState<any[]>([]);
  const [races, setRaces] = useState<any[]>([]);
  const [ethnicities, setEthnicities] = useState<any[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [levels, setLevels] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [rooms, setRooms] = useState<any[]>([]);
  const [beds, setBeds] = useState<any[]>([]);
  const [filteredBeds, setFilteredBeds] = useState<any[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBanning, setIsBanning] = useState(false);
  const [memberToBan, setMemberToBan] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [userFields, setUserFields] = useState<UserFields>({
    firstName: "",
    lastName: "",
    preferredName: "",
    phone: null,
    intakeDate: undefined,
    exitDate: undefined,
    birthDate: undefined,
    relationship: "",
    gender: "",
    ethnicity: "",
    race: "",
    school: "",
    grade: "",
    program: null,
    level: null,
    job: null,
    room: "",
    bed: null,
    isBanned: false,
  });

  useEffect(() => {
    getRelationships();
    getGenders();
    getRaces();
    getEthnicities();
    getPrograms();
    getJobs();
    if (auth?.profile.includeHMIS === 1) {
      getBeds();
    }
  }, [user]);

  const getRelationships = () => {
    fetchRelationships().then((res: any) => {
      setRelationships(res[0].data);
    });
  };

  const getGenders = () => {
    fetchGenders().then((res: any) => {
      setGenders(res[0].data);
    });
  };

  const getRaces = () => {
    fetchRaces().then((res: any) => {
      setRaces(res[0].data);
    });
  };

  const getEthnicities = () => {
    fetchEthnicities().then((res: any) => {
      setEthnicities(res[0].data);
    });
  };

  const getPrograms = () => {
    fetchPrograms()
      .then((res: any) => {
        let programs = res[0].data;
        const updatedPrograms = [{ id: 0, key: 0, name: "Select from List", programLevels: [] }, ...programs];
        setPrograms(updatedPrograms);
        setLevels([]);

        const selectedProgram = updatedPrograms.find((p: any) => p.key === user?.program);
        if (selectedProgram) {
          if (selectedProgram.programLevels && selectedProgram.programLevels.length > 0) {
            setLevels(selectedProgram.programLevels);
            if (user?.level_id) {
              const selectedLevel = selectedProgram.programLevels.find((lvl: any) => lvl.id === user.level_id);
              if (selectedLevel) {
                setUserFields((prev) => ({ ...prev, level: selectedLevel }));
              } else {
                setUserFields((prev) => ({ ...prev, level: null }));
              }
            }
          }
          setUserFields((prev) => ({ ...prev, program: selectedProgram }));
        }
        setSaveIsEnabled(false);
        setUserToSave(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getJobs = async () => {
    fetchJobs().then((res: any) => {
      const jobs = res[0].data;
      var availJobs = jobs.filter((j: any) => j.positions_filled < j.positions_total);

      //Ensure user's job is included
      const userJob = jobs.find((j: any) => j.key === user?.job?.key);

      if (userJob && !availJobs.some((j: any) => j.id === userJob.id)) {
        availJobs.push(userJob);
      }

      availJobs = [{ id: 0, key: 0, name: "Select from List" }, ...availJobs];
      setJobs(availJobs);

      const selectedJob = availJobs.find((j: any) => j.key === user?.job?.key);
      if (selectedJob) {
        setUserFields((prev) => ({ ...prev, job: selectedJob }));
      } else if (user?.job) {
        const currentJob = jobs.find((j: any) => j.key === user?.job?.key);
        setUserFields((prev) => ({ ...prev, job: currentJob }));
        setJobs((prevJobs) => [currentJob, ...prevJobs]);
      } else {
        // setUserFields((prev) => ({ ...prev, job: { key: "", name: "" } }));
      }
    });
  };

  const getBeds = () => {
    fetchBeds()
      .then((res: any) => {
        var bedList = res[0].data;
        //include bed user is currently assigned to
        var currentBed;
        if (user?.bed) {
          currentBed = bedList.find((b: any) => b.key === user?.bed?.key);
        }
        //filter to vacant beds
        bedList = bedList.filter((b: any) => b.occupant_id === null);

        if (currentBed) {
          //if user is assigned to a bed, add it to the list
          bedList.push(currentBed);
        }

        bedList.sort((a: any, b: any) => {
          // group by room name
          if (a.room_name !== b.room_name) {
            return a.room_name.localeCompare(b.room_name, undefined, { numeric: true, sensitivity: "base" });
          }
          // sort beds in each room alphanumerically
          return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
        });

        setBeds(bedList);
        if (user?.room) {
          const filtered = bedList.filter((b: any) => b.room_key === user?.room);
          setFilteredBeds(filtered);
        } else {
          setFilteredBeds(bedList);
        }
        getRooms();
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setErrMsg("");
      });
  };

  const getRooms = () => {
    fetchRooms()
      .then((res: any) => {
        const roomList = res[0].data;
        roomList.sort((a: any, b: any) => {
          return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
        });
        setRooms(roomList);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  useEffect(() => {
    if (!user) return;

    setUserFields({
      firstName: user.firstname ?? "",
      lastName: user.lastname ?? "",
      preferredName: user.preferredname ?? "",
      phone: user.phonenumber === "" ? null : user.phonenumber,
      intakeDate: user.intake_at,
      exitDate: user.exit_at,
      birthDate: user.birthdate,
      relationship: user.relationship ?? "",
      gender: user.gender ?? "",
      ethnicity: user.ethnicity ?? "",
      race: user.race ?? "",
      school: user.school ?? "",
      grade: user.grade ?? "",
      job: user.job ?? null,
      ...(auth?.profile.includeHMIS === 1 && {
        room: user.room ?? "",
        bed: user.bed ?? null,
      }),
      isBanned: user.is_banned === 1,
    });

    if (user.profile.length > 0) {
      user?.profile?.map((p: any) => {
        const base64String = "data:image/jpeg;base64, " + p?.data;
        setProfileImage(base64String);
      });
    } else {
      setProfileImage("");
    }

    if (user.customfields) {
      setCustom(user.customfields);
    }
  }, [user, auth]);

  function validateUser(
    first: string,
    last: string,
    emailVal: any,
    phoneVal: any,
    level: any,
    levelRequired: boolean,
    appAccess: boolean = false,
    fieldType: string = "user",
  ) {
    let errors: string[] = [];
    errors = validateUserFields(first, last, emailVal, phoneVal, level, levelRequired, appAccess);

    if (setUserToSave) setUserToSave(true);
    if (fieldType === "custom") {
      setCustomUpdated(true);
    } else {
      setUserUpdated(true);
    }

    return {
      isValid: errors.length === 0,
      errors: errors,
    };
  }

  const updateField = (field: keyof typeof userFields, value: UserFields[keyof UserFields], levelRequired = levels.length > 0) => {
    setUserFields((prev) => {
      const updated = { ...prev, [field]: value };
      const validation = validateUser(
        updated.firstName,
        updated.lastName,
        updated.email,
        updated.phone,
        updated.level,
        levelRequired,
        updated.appAccess,
      );
      setSaveIsEnabled(validation.isValid);
      if (setUserToSave) setUserToSave(true);
      return updated;
    });
  };

  const updateProgram = (e: any) => {
    updateField("level", null);
    let updatedLevels = [];
    setLevels([]);
    if (e.programLevels && e.programLevels.length > 0) {
      updatedLevels = e.programLevels;
    }
    setLevels(updatedLevels);
    updateField("program", e, updatedLevels.length > 0);
  };

  const updateLevel = (e: any) => {
    if (e.key !== userFields.level?.key) {
      updateField("level", e);
    }
  };

  const updateJob = (e: any) => {
    if (e.key !== userFields.job?.key) {
      updateField("job", e);
    }
  };

  const updateRoom = (e: any) => {
    updateField("room", e.key);
    updateField("bed", null);
    const filtered = beds.filter((b: any) => b.room_key === e.key);
    setFilteredBeds(filtered);
  };

  const updateBed = (e: any) => {
    updateField("bed", e);
  };

  const saveMember = () => {
    const validation = validateUser(userFields.firstName, userFields.lastName, "", userFields.phone, userFields.level, levels.length > 0);
    setErrMsg("");

    if (validation.isValid) {
      if (userUpdated) {
        const updatedMember: any = {
          firstname: userFields.firstName,
          lastname: userFields.lastName,
          preferredname: userFields.preferredName,
          phonenumber: userFields.phone === "1" ? null : userFields.phone,
          intake_at: userFields.intakeDate,
          exit_at: userFields.exitDate,
          birthdate: userFields.birthDate,
          relationship: userFields.relationship,
          gender: userFields.gender,
          ethnicity: userFields.ethnicity,
          race: userFields.race,
          school: userFields.school,
          grade: userFields.grade,
          program: userFields.program?.key === 0 ? 0 : userFields.program?.key ? userFields.program.key : null,
          level_id: userFields.level?.id === 0 ? 0 : userFields.level?.id ? userFields.level.id : null,
        };

        if (updatedMember.program === 0) {
          updatedMember.level_id = null;
        }

        patchMember(updatedMember, id, user?.id)
          .then((res: any) => {})
          .catch((err: any) => {
            setErrMsg(err?.response?.data?.msg);
          });

        //job has changed
        if (user?.job?.key !== userFields.job?.key) {
          //remove from previous job
          if (user?.job?.job_assignment_id) {
            const previousJob = {
              member_id: user?.id,
            };

            patchJobAssignment(previousJob, id, user?.job?.job_assignment_id)
              .then((res: any) => {})
              .catch((err: any) => {
                setErrMsg(err?.response?.data?.msg);
              });
          }
          //new job
          if (userFields.job && userFields.job?.id !== 0) {
            const newJob = {
              job_id: userFields.job.id,
              member_id: user?.id,
            };

            postJobAssignment(newJob, id)
              .then((res: any) => {})
              .catch((err: any) => {
                setErrMsg(err?.response?.data?.msg);
              });
          }
        }

        if (auth?.profile.includeHMIS === 1) {
          //bed changed
          if (user?.bed?.key !== userFields.bed?.key) {
            //remove from previous bed
            if (user?.bed?.bed_occupant_id) {
              patchBedOccupant(id, user?.bed?.bed_occupant_id)
                .then((res: any) => {})
                .catch((err: any) => {
                  setErrMsg(err?.response?.data?.msg);
                });
            }
            //new bed
            if (userFields.bed) {
              const newBed = {
                bed_id: userFields.bed?.id,
                member_id: user?.id,
              };

              postBedOccupant(newBed, id)
                .then((res: any) => {})
                .catch((err: any) => {
                  setErrMsg(err?.response?.data?.msg);
                });
            }
          }
        }
      }

      //update / create custom field values
      if (customUpdated) {
        custom.map((cust: any) => {
          if (cust?.field_value_id) {
            //patch
            const fieldVal = {
              value: cust?.value,
            };

            patchFieldValue(fieldVal, cust?.field_value_id).catch((err: any) => {
              console.log(err);
              setErrMsg(err?.response?.data?.msg);
            });
          } else if (cust?.value) {
            //post
            const fieldVal = {
              field_id: cust?.field_id,
              person_id: user?.id,
              person_type: "member",
              value: cust?.value,
            };

            postFieldValue(fieldVal).catch((err: any) => {
              console.log(err);
              setErrMsg(err?.response?.data?.msg);
            });
          }
        });
      }

      setUserUpdated(false);
      setCustomUpdated(false);
      setSaveIsEnabled(false);
      setUserToSave(false);
      fetchHousehold();
      setShowToast(true);
      setToastText("Member Updated");
      const toast = userFields.preferredName
        ? userFields.firstName + " '" + userFields.preferredName + "' " + userFields.lastName
        : userFields.firstName + " " + userFields.lastName;
      setToastBody(toast);
    } else {
      validation.errors.map((err: string) => {
        setErrMsg((prev) => (prev ? prev + "  |  " + err : err));
        return true;
      });
    }
  };

  const onClickBan = () => {
    setIsBanning(true);
    setMemberToBan(userFields.firstName + " " + userFields.lastName);
  };

  const onClickDelete = () => {
    setIsDeleting(true);
  };

  return (
    <>
      {isDeleting ? (
        <ConfirmDelete
          user={user}
          family={members}
          setIsDeleting={setIsDeleting}
          fetchHousehold={fetchHousehold}
          resetHouseholdTab={resetHouseholdTab}
          caseStatus={currentCase?.casestatus}
          isLastUser={isLastUser}
          setShowToast={setShowToast}
          setToastText={setToastText}
          setToastBody={setToastBody}
        />
      ) : isBanning ? (
        <ConfirmBan
          user={user}
          previousJob={user?.job?.key ?? ""}
          isBanned={userFields.isBanned ?? false}
          setIsBanning={setIsBanning}
          userToBan={memberToBan}
          setUserToBan={setMemberToBan}
          fetchHousehold={fetchHousehold}
          resetCurrentView={resetHouseholdTab}
          type="Member"
          caseStatus={currentCase?.casestatus}
          setShowToast={setShowToast}
          setToastText={setToastText}
          setToastBody={setToastBody}
        />
      ) : isLoading ? (
        <div style={{ minHeight: "400px" }}>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </div>
      ) : (
        <div>
          <Stack gap={3} className="mb-3">
            <Row>
              <Col>
                <h4 className="app-header-text">{heading}</h4>
              </Col>
              <Col className="text-end">
                <Stack className="stack-float" direction="horizontal" gap={3}>
                  {myCase && (
                    <>
                      <Button className="app-danger-color button" onClick={() => onClickDelete()}>
                        Delete
                      </Button>
                      <div className="vr" />
                      <Button className="app-secondary-color button" onClick={() => saveMember()} disabled={!saveIsEnabled}>
                        Save
                      </Button>
                      <div className="vr" />
                    </>
                  )}
                  <Button className="app-primary-color button" onClick={() => onClickDoneEdit()}>
                    Cancel
                  </Button>
                </Stack>
              </Col>
            </Row>
            <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
              {errMsg}
            </p>
            <Stack gap={3}>
              <Row className="h-auto">
                <Col xs={4}>
                  <Input
                    label="First Name"
                    type="text"
                    value={userFields.firstName}
                    onUpdate={(e: any) => updateField("firstName", e.target.value)}
                    required={true}
                    disabled={myCase ? false : true}
                  />
                </Col>
                <Col xs={4}>
                  <Input
                    label="Last Name"
                    type="text"
                    value={userFields.lastName}
                    onUpdate={(e: any) => updateField("lastName", e.target.value)}
                    required={true}
                    disabled={myCase ? false : true}
                  />
                </Col>
                <Col xs={4}>
                  <Input
                    label="Preferred Name"
                    type="text"
                    value={userFields.preferredName}
                    onUpdate={(e: any) => updateField("preferredName", e.target.value)}
                    disabled={myCase ? false : true}
                  />
                </Col>
              </Row>
              <Row className="h-auto">
                <Col xs={8}>
                  <Stack gap={3}>
                    <Row>
                      <Col xs={6}>
                        <InputPhone
                          label="Phone"
                          value={userFields.phone}
                          onUpdate={(e: any) => updateField("phone", e)}
                          disabled={myCase ? false : true}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={6}>
                        <DatePicker
                          label="Intake Date"
                          currentValue={userFields.intakeDate}
                          setCurrentValue={(e: any) => updateField("intakeDate", e)}
                          hasMaxDate={true}
                          disabled={currentCase?.casestatus === "active" && myCase ? false : true}
                        />
                      </Col>
                      {currentCase?.casestatus !== "active" && (
                        <Col xs={6}>
                          <DatePicker
                            label="Exit Date"
                            currentValue={userFields.exitDate}
                            setCurrentValue={(e: any) => updateField("exitDate", e)}
                            hasMaxDate={true}
                            disabled={true}
                          />
                        </Col>
                      )}
                      <Col className={currentCase?.casestatus !== "active" ? "col-6 mt-3" : "col-6"}>
                        <DatePicker
                          label="Birth Date"
                          currentValue={userFields.birthDate}
                          setCurrentValue={(e: any) => updateField("birthDate", e)}
                          hasMaxDate={true}
                          disabled={myCase ? false : true}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col className="col-6">
                        <SelectObject
                          label="Relationship"
                          value={userFields.relationship}
                          options={relationships}
                          onUpdate={(e: any) => updateField("relationship", e.target.value)}
                          disabled={myCase ? false : true}
                        />
                      </Col>
                      <Col className="col-6">
                        <SelectObject
                          label="Gender"
                          value={userFields.gender}
                          options={genders}
                          onUpdate={(e: any) => updateField("gender", e.target.value)}
                          disabled={myCase ? false : true}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col className="col-6">
                        <SelectObject
                          label="Ethnicity"
                          value={userFields.ethnicity}
                          options={ethnicities}
                          onUpdate={(e: any) => updateField("ethnicity", e.target.value)}
                          disabled={myCase ? false : true}
                        />
                      </Col>
                      <Col className="col-6">
                        <SelectObject
                          label="Race"
                          value={userFields.race}
                          options={races}
                          onUpdate={(e: any) => updateField("race", e.target.value)}
                          disabled={myCase ? false : true}
                        />
                      </Col>
                    </Row>
                  </Stack>
                </Col>
                <Col xs={4}>
                  <CaptureProfilePic
                    userId={user?.id}
                    userProfileId={user?.profile_id}
                    userName={userFields.firstName + " " + userFields.lastName}
                    userType="member"
                    myCase={myCase}
                    profileImage={profileImage}
                    setProfileImage={setProfileImage}
                    setErrMsg={setErrMsg}
                    setShowToast={setShowToast}
                    setToastText={setToastText}
                    setToastBody={setToastBody}
                  />
                </Col>
              </Row>
              {userFields.relationship === "child" && (
                <Row className="h-auto">
                  <Col xs={4}>
                    <Input
                      label="School"
                      type="text"
                      value={userFields.school}
                      onUpdate={(e: any) => updateField("school", e.target.value)}
                      disabled={myCase ? false : true}
                    />
                  </Col>
                  <Col xs={4}>
                    <Input
                      label="Grade"
                      type="text"
                      value={userFields.grade}
                      onUpdate={(e: any) => updateField("grade", e.target.value)}
                      disabled={myCase ? false : true}
                    />
                  </Col>
                </Row>
              )}
              {(programs.length > 0 || jobs.length > 0) && (
                <Row className="h-auto">
                  {programs.length > 0 && (
                    <Col xs={4}>
                      <SelectObjectTypeahead
                        label="Program"
                        value={userFields.program}
                        options={programs}
                        onUpdate={(e: any) => updateProgram(e)}
                        disabled={userFields.isBanned ? true : myCase ? false : true}
                      />
                    </Col>
                  )}
                  {levels.length > 0 && (
                    <Col xs={4}>
                      <SelectObjectTypeahead
                        label="Program Level"
                        value={userFields.level}
                        options={levels}
                        onUpdate={(e: any) => updateLevel(e)}
                        disabled={userFields.isBanned ? true : myCase ? false : true}
                      />
                    </Col>
                  )}
                  {jobs.length > 0 && (
                    <Col xs={4}>
                      <SelectObjectTypeahead
                        label="Job"
                        value={userFields.job?.key}
                        options={jobs}
                        onUpdate={(e: any) => updateJob(e)}
                        disabled={userFields.isBanned ? true : myCase ? false : true}
                      />
                    </Col>
                  )}
                </Row>
              )}

              {rooms.length > 0 && beds.length > 0 && (
                <Row className="h-auto">
                  <Col xs={4}>
                    <SelectObjectTypeahead
                      label="Room"
                      value={userFields.room}
                      options={rooms}
                      onUpdate={(e: any) => updateRoom(e)}
                      disabled={userFields.isBanned ? true : myCase ? false : true}
                    />
                  </Col>
                  <Col xs={4}>
                    <SelectObjectTypeahead
                      label="Bed"
                      value={userFields.bed?.key}
                      options={filteredBeds}
                      onUpdate={(e: any) => updateBed(e)}
                      disabled={userFields.room ? (myCase ? false : true) : true}
                      initialOption="Vacant"
                    />
                  </Col>
                </Row>
              )}
            </Stack>
          </Stack>
          {user?.customfields?.length > 0 && (
            <Row className="h-auto">
              {user?.customfields?.map((c: any) => (
                <CustomField
                  customField={c}
                  setCustom={setCustom}
                  userId={user?.id}
                  userType={"member"}
                  enableSave={() =>
                    validateUser(
                      userFields.firstName,
                      userFields.lastName,
                      "",
                      userFields.phone,
                      userFields.level,
                      levels.length > 0,
                      false,
                      "custom",
                    )
                  }
                  setSaveIsEnabled={setSaveIsEnabled}
                  myCase={myCase}
                />
              ))}
            </Row>
          )}
          <Row className="h-auto">
            <Col xs={4} className="text-start">
              <Stack direction="horizontal" gap={3}>
                {(auth?.profile?.role === "org_admin" || auth?.profile?.role === "admin") && (
                  <Button className="app-danger-color-inverse button" onClick={() => onClickBan()}>
                    {userFields.isBanned ? "Unban" : "Ban"}
                  </Button>
                )}
                <small className="text-muted">{user?.number_infractions + " Infraction(s)"}</small>
              </Stack>
            </Col>
          </Row>
        </div>
      )}
    </>
  );
};

export default EditMember;
